import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/utils/trpc";

export const Route = createFileRoute("/_protected/session/$sessionId")({
  component: SessionDetailComponent,
});

function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(startedAt: Date | string, completedAt: Date | string | null): string {
  if (!completedAt) return "In Progress";

  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;

  const minutes = Math.floor(durationMs / (1000 * 60));
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function SessionDetailComponent() {
  const { sessionId } = Route.useParams();

  const sessionQuery = useQuery(
    trpc.sessions.get.queryOptions({ sessionId })
  );

  if (sessionQuery.isLoading) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <div className="h-6 w-24 bg-muted animate-pulse rounded mb-6" />
        <Card>
          <CardHeader>
            <div className="h-7 w-48 bg-muted animate-pulse rounded" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="h-4 w-64 bg-muted animate-pulse rounded" />
              <div className="h-4 w-48 bg-muted animate-pulse rounded" />
            </div>
          </CardContent>
        </Card>
        <div className="mt-6 space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-5 w-32 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-4 w-full bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (sessionQuery.error) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          Back to Dashboard
        </Link>
        <Card className="mt-6">
          <CardContent className="py-6">
            <p className="text-destructive">
              Failed to load session: {sessionQuery.error.message}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!sessionQuery.data) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          Back to Dashboard
        </Link>
        <Card className="mt-6">
          <CardContent className="py-6">
            <p className="text-muted-foreground">Session not found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const session = sessionQuery.data;

  // Group sets by exercise
  const setsByExercise = session.sets.reduce((acc, set) => {
    const exerciseId = set.exerciseId;
    if (!acc[exerciseId]) {
      acc[exerciseId] = {
        exerciseName: set.exercise.name,
        sets: [],
      };
    }
    acc[exerciseId].sets.push(set);
    return acc;
  }, {} as Record<string, { exerciseName: string; sets: typeof session.sets }>);

  // Sort sets within each exercise by set number
  Object.values(setsByExercise).forEach((group) => {
    group.sets.sort((a, b) => a.setNumber - b.setNumber);
  });

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
        Back to Dashboard
      </Link>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{session.template.name}</span>
            {!session.completedAt && (
              <span className="text-xs font-normal bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 px-2 py-0.5 rounded">
                In Progress
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>{formatDate(session.startedAt)}</p>
            <p>
              Started at {formatTime(session.startedAt)}
              {session.completedAt && ` · Completed at ${formatTime(session.completedAt)}`}
            </p>
            <p>Duration: {formatDuration(session.startedAt, session.completedAt)}</p>
          </div>
        </CardContent>
      </Card>

      <h2 className="text-lg font-semibold mt-8 mb-4">Exercises</h2>

      {Object.keys(setsByExercise).length === 0 ? (
        <Card>
          <CardContent className="py-6">
            <p className="text-muted-foreground">No sets logged for this session.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(setsByExercise).map(([exerciseId, group]) => (
            <Card key={exerciseId}>
              <CardHeader>
                <CardTitle>{group.exerciseName}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {group.sets.map((set) => (
                    <div
                      key={set.id}
                      className="flex items-center justify-between text-sm py-1 border-b border-muted last:border-0"
                    >
                      <span className="text-muted-foreground">Set {set.setNumber}</span>
                      <div className="flex items-center gap-4">
                        <span>{set.weight} lbs</span>
                        <span>{set.reps} reps</span>
                        {set.rpe && (
                          <span className="text-muted-foreground">RPE {set.rpe}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
